import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Pill from "@/components/Pill";
import { Check, X, ClipboardCheck, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import TaskTicketLightbox from "@/components/TaskTicketLightbox";
import OPATimelineDialog from "@/components/OPATimelineDialog";

function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function OPATable({ rows, loading, showActions, onApprove, onReject, onView, onPreview, onOpenTimeline }) {
  return (
    <div className="w-full overflow-hidden border border-white/10 rounded-lg bg-zinc-900">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-zinc-950/50 border-b border-white/10 text-left">
            <th className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">OPA ID</th>
            <th className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Task / Ticket</th>
            <th className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Purpose</th>
            <th className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Amount</th>
            <th className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Requested By</th>
            <th className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Created</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
            </td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500 text-sm">No requests in this list.</td></tr>
          ) : rows.map((o) => (
            <tr key={o.id} className="border-b border-white/5 hover:bg-white/[0.025] transition-colors" data-testid={`approval-row-${o.id}`}>
              <td className="px-4 py-3 font-mono text-xs">
                <button
                  type="button"
                  onClick={() => onOpenTimeline(o)}
                  className="text-sky-400 hover:underline cursor-pointer"
                  data-testid={`approval-id-${o.id}`}
                  title="View OPA timeline"
                >
                  {o.code}
                </button>
              </td>
              <td className="px-4 py-3 text-xs">
                {o.task_id ? (
                  <button
                    type="button"
                    onClick={() => onPreview({ type: "task", id: o.task_id })}
                    className="font-mono text-sky-400 hover:underline"
                    data-testid={`approval-link-task-${o.id}`}
                  >
                    {o.task_code || "Task"}
                  </button>
                ) : o.issue_id ? (
                  <button
                    type="button"
                    onClick={() => onPreview({ type: "ticket", id: o.issue_id })}
                    className="font-mono text-amber-400 hover:underline"
                    data-testid={`approval-link-ticket-${o.id}`}
                  >
                    {o.issue_code || "Ticket"}
                  </button>
                ) : "—"}
              </td>
              <td className="px-4 py-3 text-zinc-300 max-w-[260px] truncate" title={o.purpose || ""}>{o.purpose || "—"}</td>
              <td className="px-4 py-3 font-mono text-white">{fmtINR(o.amount)}</td>
              <td className="px-4 py-3 text-zinc-400 text-xs">{o.requested_by_name || "—"}</td>
              <td className="px-4 py-3 text-zinc-500 text-xs">{fmtDate(o.created_at)}</td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <button
                  onClick={() => onView(o)}
                  className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/5 rounded mr-1"
                  title="View details"
                  data-testid={`approval-view-${o.id}`}
                >
                  <Eye className="w-4 h-4" />
                </button>
                {showActions && (
                  <>
                    <button
                      onClick={() => onApprove(o)}
                      className="p-1.5 text-emerald-400 hover:bg-emerald-500/10 rounded mr-1"
                      title="Approve"
                      data-testid={`approval-approve-${o.id}`}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onReject(o)}
                      className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"
                      title="Reject"
                      data-testid={`approval-reject-${o.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ApprovalsPage() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ pending: [], approved: [], rejected: [] });
  const [loading, setLoading] = useState(true);

  // Per-scope visibility from /api/me/modules — controls which scope buttons
  // (OPA / Project / Task / Ticket) are shown.
  const [approvalsVisible, setApprovalsVisible] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/me/modules").then(({ data }) => {
      if (!cancelled) setApprovalsVisible(data.approvals_visible || {});
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Detail view dialog
  const [viewing, setViewing] = useState(null); // full OPA with task/issue
  const [viewLoading, setViewLoading] = useState(false);

  // Reject dialog
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Approve confirm
  const [approving, setApproving] = useState(null);

  // Task / Ticket read-only lightbox
  const [previewing, setPreviewing] = useState(null);
  // OPA timeline
  const [timelineOpa, setTimelineOpa] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, r] = await Promise.all([
        api.get("/opa-requests", { params: { status: "pending", for_approval: true } }),
        api.get("/opa-requests", { params: { status: "approved", for_approval: true } }),
        api.get("/opa-requests", { params: { status: "rejected", for_approval: true } }),
      ]);
      setData({ pending: p.data, approved: a.data, rejected: r.data });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openView = async (opa) => {
    setViewLoading(true);
    setViewing({ ...opa, _loading: true });
    try {
      const { data: full } = await api.get(`/opa-requests/${opa.id}`);
      setViewing(full);
    } catch (e) {
      toast.error("Failed to load OPA details");
      setViewing(null);
    } finally {
      setViewLoading(false);
    }
  };

  const doApprove = async () => {
    if (!approving) return;
    setSubmitting(true);
    try {
      await api.patch(`/opa-requests/${approving.id}/approve`);
      toast.success(`Approved ${approving.code}`);
      setApproving(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Approval failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Project Completion approvals
  const [scope, setScope] = useState("opa"); // "opa" | "projects"
  const [pendingProjects, setPendingProjects] = useState([]);
  const [confirmedProjects, setConfirmedProjects] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [confirmingProject, setConfirmingProject] = useState(null);

  // If the currently-selected scope becomes hidden after /me/modules loads,
  // switch to the first visible scope automatically. Also drops legacy
  // `task`/`ticket` scopes that were removed from the UI.
  useEffect(() => {
    if (scope === "task" || scope === "ticket") { setScope("opa"); return; }
    if (!approvalsVisible) return;
    const map = {
      opa: approvalsVisible.opa,
      projects: approvalsVisible.project,
    };
    if (!map[scope]) {
      const fallback = Object.keys(map).find((k) => map[k]);
      if (fallback && fallback !== scope) setScope(fallback);
    }
  }, [approvalsVisible, scope]);

  const loadProjects = useCallback(async () => {
    setProjectLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/biz-projects", { params: { only_completed: true } }),
        api.get("/biz-projects", { params: { only_confirmed: true } }),
      ]);
      setPendingProjects(p.data);
      setConfirmedProjects(c.data);
    } finally {
      setProjectLoading(false);
    }
  }, []);

  useEffect(() => {
    if (scope === "projects") loadProjects();
  }, [scope, loadProjects]);

  const doConfirmProject = async () => {
    if (!confirmingProject) return;
    setSubmitting(true);
    try {
      await api.patch(`/biz-projects/${confirmingProject.id}/confirm-completion`);
      toast.success(`Confirmed ${confirmingProject.code}`);
      setConfirmingProject(null);
      loadProjects();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Confirm failed");
    } finally {
      setSubmitting(false);
    }
  };

  const doReject = async () => {
    if (!rejecting) return;
    if (!rejectReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/opa-requests/${rejecting.id}/reject`, { reason: rejectReason.trim() });
      toast.success(`Rejected ${rejecting.code}`);
      setRejecting(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Rejection failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
            <ClipboardCheck className="w-7 h-7 text-sky-400" /> Approvals
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {scope === "opa" && "Review OPA fund requests raised against Tasks and Tickets."}
            {scope === "projects" && "Confirm completed Projects before they are signed off."}
          </p>
        </div>
      </header>

      <Tabs value={scope} onValueChange={setScope} data-testid="approvals-scope">
        <TabsList className="bg-zinc-900 border border-white/10">
          {(approvalsVisible?.opa ?? true) && (
            <TabsTrigger value="opa" data-testid="approvals-scope-opa" className="data-[state=active]:bg-white data-[state=active]:text-black">
              OPA Approval
              <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">{data.pending.length}</span>
            </TabsTrigger>
          )}
          {(approvalsVisible?.project ?? true) && (
            <TabsTrigger value="projects" data-testid="approvals-scope-projects" className="data-[state=active]:bg-white data-[state=active]:text-black">
              Project Completion Approval
              <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">{pendingProjects.length}</span>
            </TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {scope === "opa" ? (
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-900 border border-white/10">
          <TabsTrigger value="pending" data-testid="approvals-tab-pending" className="data-[state=active]:bg-white data-[state=active]:text-black">
            Pending Approval
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">{data.pending.length}</span>
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="approvals-tab-approved" className="data-[state=active]:bg-white data-[state=active]:text-black">
            Already Approved
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">{data.approved.length}</span>
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="approvals-tab-rejected" className="data-[state=active]:bg-white data-[state=active]:text-black">
            Rejected
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">{data.rejected.length}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <OPATable rows={data.pending} loading={loading} showActions onApprove={setApproving} onReject={(o) => { setRejecting(o); setRejectReason(""); }} onView={openView} onPreview={setPreviewing} onOpenTimeline={setTimelineOpa} />
        </TabsContent>
        <TabsContent value="approved" className="mt-4">
          <OPATable rows={data.approved} loading={loading} showActions={false} onView={openView} onPreview={setPreviewing} onOpenTimeline={setTimelineOpa} />
        </TabsContent>
        <TabsContent value="rejected" className="mt-4">
          <OPATable rows={data.rejected} loading={loading} showActions={false} onView={openView} onPreview={setPreviewing} onOpenTimeline={setTimelineOpa} />
        </TabsContent>
      </Tabs>
      ) : (
        <ProjectCompletionsTable
          pending={pendingProjects}
          confirmed={confirmedProjects}
          loading={projectLoading}
          onConfirm={setConfirmingProject}
        />
      )}

      <TaskTicketLightbox open={previewing} onClose={() => setPreviewing(null)} />
      <OPATimelineDialog opa={timelineOpa} onClose={() => setTimelineOpa(null)} />


      {/* Approve confirm */}
      <AlertDialog open={!!approving} onOpenChange={(o) => !o && setApproving(null)}>
        <AlertDialogContent className="bg-zinc-950 border-white/10 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Approve OPA?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              You are approving <span className="font-mono text-sky-400">{approving?.code}</span> for <span className="font-mono text-white">{fmtINR(approving?.amount)}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-white/10 text-zinc-300 hover:bg-zinc-800" data-testid="approve-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={submitting}
              onClick={doApprove}
              className="bg-emerald-500 text-black hover:bg-emerald-400"
              data-testid="approve-confirm"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Confirm Approval
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject with reason */}
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent className="bg-zinc-950 border-white/10 text-zinc-100 sm:max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Reject OPA</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Provide a reason for rejecting <span className="font-mono text-sky-400">{rejecting?.code}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Label className="text-xs text-zinc-400">Reason for Rejection <span className="text-red-400">*</span></Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g., Budget exhausted, awaiting CFO clearance, duplicate request…"
              className="mt-1.5 bg-zinc-900 border-white/10 text-white min-h-[110px]"
              data-testid="reject-reason"
              autoFocus
            />
          </div>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setRejecting(null)} disabled={submitting} className="text-zinc-400 hover:text-white" data-testid="reject-cancel">Cancel</Button>
            <Button onClick={doReject} disabled={submitting || !rejectReason.trim()} className="bg-red-500 text-black hover:bg-red-400 font-medium" data-testid="reject-confirm">
              {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail view */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-zinc-950 border-white/10 text-zinc-100 sm:max-w-2xl" data-testid="opa-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              OPA <span className="font-mono text-sky-400">{viewing?.code}</span>
              {viewing?.status && <Pill kind="status" value={viewing.status === "approved" ? "Approved" : viewing.status === "rejected" ? "Rejected" : "Open"} />}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">Linked Task / Ticket details below.</DialogDescription>
          </DialogHeader>
          {viewLoading || viewing?._loading ? (
            <div className="py-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>
          ) : viewing && (
            <div className="space-y-5 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">Amount</div>
                  <div className="text-lg font-mono text-emerald-400">{fmtINR(viewing.amount)}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">Requested By</div>
                  <div className="text-zinc-200">{viewing.requested_by_name || "—"}</div>
                  <div className="text-xs text-zinc-500">{fmtDate(viewing.created_at)}</div>
                </div>
              </div>
              {viewing.purpose && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">Purpose</div>
                  <div className="text-zinc-200 whitespace-pre-wrap">{viewing.purpose}</div>
                </div>
              )}
              {viewing.status === "approved" && (
                <div className="rounded-md border border-emerald-500/20 bg-emerald-500/5 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-emerald-400 mb-0.5">Approved</div>
                  <div className="text-zinc-200 text-sm">By {viewing.approved_by_name || "—"} on {fmtDate(viewing.approved_at)}</div>
                </div>
              )}
              {viewing.status === "rejected" && (
                <div className="rounded-md border border-red-500/20 bg-red-500/5 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-red-400 mb-0.5">Rejected</div>
                  <div className="text-zinc-200 text-sm">By {viewing.rejected_by_name || "—"} on {fmtDate(viewing.rejected_at)}</div>
                  {viewing.rejection_reason && (
                    <div className="text-zinc-300 text-sm mt-2 italic">&ldquo;{viewing.rejection_reason}&rdquo;</div>
                  )}
                </div>
              )}
              {viewing.task && (
                <div className="border-t border-white/5 pt-4">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Linked Task</div>
                  <div className="rounded-md border border-white/10 p-3 space-y-1 bg-zinc-900">
                    <Link to={`/tasks/${viewing.task.id}`} className="font-mono text-xs text-sky-400 hover:underline" data-testid="opa-detail-task-link">{viewing.task.code}</Link>
                    <div className="text-white font-medium">{viewing.task.subject}</div>
                    <div className="flex gap-2 flex-wrap mt-1">
                      <Pill kind="priority" value={viewing.task.priority} />
                      <Pill kind="status" value={viewing.task.status} />
                      {viewing.task.due_date && <span className="text-xs text-zinc-500 self-center">Due {viewing.task.due_date}</span>}
                    </div>
                    {viewing.task.details && (
                      <p className="text-xs text-zinc-400 mt-2 whitespace-pre-wrap line-clamp-4">{viewing.task.details}</p>
                    )}
                  </div>
                </div>
              )}
              {viewing.issue && (
                <div className="border-t border-white/5 pt-4">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Linked Ticket</div>
                  <div className="rounded-md border border-white/10 p-3 space-y-1 bg-zinc-900">
                    <Link to={`/issues/${viewing.issue.id}`} className="font-mono text-xs text-amber-400 hover:underline" data-testid="opa-detail-ticket-link">{viewing.issue.code}</Link>
                    <div className="text-white font-medium">{viewing.issue.subject}</div>
                    <div className="flex gap-2 flex-wrap mt-1">
                      <Pill kind="priority" value={viewing.issue.priority} />
                      <Pill kind="status" value={viewing.issue.status} />
                    </div>
                    {viewing.issue.description && (
                      <p className="text-xs text-zinc-400 mt-2 whitespace-pre-wrap line-clamp-4">{viewing.issue.description}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm project completion */}
      <AlertDialog open={!!confirmingProject} onOpenChange={(o) => !o && setConfirmingProject(null)}>
        <AlertDialogContent className="bg-zinc-950 border-white/10 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirm project completion?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Marking <span className="font-mono text-sky-400">{confirmingProject?.code}</span> as <span className="text-cyan-300">Completed &amp; Confirmed</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-white/10 text-zinc-300 hover:bg-zinc-800" data-testid="confirm-project-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={submitting}
              onClick={doConfirmProject}
              className="bg-cyan-500 text-black hover:bg-cyan-400"
              data-testid="confirm-project-confirm"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function fmtDateOnly(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
}

function ProjectCompletionsTable({ pending, confirmed, loading, onConfirm }) {
  const [tab, setTab] = useState("pending");
  const rows = tab === "pending" ? pending : confirmed;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2" data-testid="project-completion-tabs">
        <button
          onClick={() => setTab("pending")}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
            tab === "pending" ? "bg-white text-black border-white" : "bg-zinc-900 text-zinc-300 border-white/10 hover:border-white/30"
          }`}
          data-testid="project-completion-tab-pending"
        >
          Pending Confirmation
          <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">{pending.length}</span>
        </button>
        <button
          onClick={() => setTab("confirmed")}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
            tab === "confirmed" ? "bg-white text-black border-white" : "bg-zinc-900 text-zinc-300 border-white/10 hover:border-white/30"
          }`}
          data-testid="project-completion-tab-confirmed"
        >
          Confirmed
          <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300">{confirmed.length}</span>
        </button>
      </div>

      <div className="w-full overflow-hidden border border-white/10 rounded-lg bg-zinc-900">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-950/50 border-b border-white/10 text-left">
              {["Project ID", "Project Name", "Customer", "Completion Report #", "Completed On", "Action"].map((c, i) => (
                <th key={i} className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-500 text-sm">
                {tab === "pending" ? "No projects awaiting confirmation." : "No confirmed projects yet."}
              </td></tr>
            ) : rows.map((p) => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.025] transition-colors" data-testid={`project-completion-row-${p.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/biz-projects/${p.id}`} className="font-mono text-xs text-sky-400 hover:underline" data-testid={`project-completion-code-${p.id}`}>
                    {p.code}
                  </Link>
                </td>
                <td className="px-4 py-3 text-white">{p.project_name || "—"}</td>
                <td className="px-4 py-3 text-zinc-300">{p.customer_name || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-violet-300">{p.completion_report_number || "—"}</td>
                <td className="px-4 py-3 text-zinc-500 text-xs">{p.completion_date_time || fmtDateOnly(p.confirmed_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {tab === "pending" ? (
                    <button
                      onClick={() => onConfirm(p)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs text-cyan-300 hover:bg-cyan-500/10 border border-cyan-500/30"
                      data-testid={`project-completion-confirm-${p.id}`}
                    >
                      <Check className="w-3.5 h-3.5" /> Confirm
                    </button>
                  ) : (
                    <span className="text-[11px] uppercase tracking-wider text-cyan-400">Confirmed</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
